/**
 * Policy pages (terms, privacy, refunds, delivery, disclaimer, about). Business
 * details come from brand.json; an empty one is left out of the text.
 *
 * DRAFT — TO BE REVIEWED BY OUR LEGAL ADVISER. Rewritten on 25 October 2026
 * for the research-tools positioning: one kind of account, one membership,
 * no investment advice, no recommendations and no calls to members. They
 * describe the platform as it works today: online payments through our
 * payment gateway (PayU; the membership starts once the payment is confirmed;
 * bank transfer switched off on 29 Oct 2026), no automatic renewal, account
 * deletion on request, Resend for email, Google Analytics 4 for site analytics.
 * Change the words here only, and keep them true to the app when it changes
 * (for example when automatic renewal is added).
 */
import { brand } from "@/brand";

export type Block = string | { list: string[] };
export interface DocSection {
  heading: string;
  body: Block[];
}
export interface LegalDoc {
  slug: string;
  title: string;
  summary: string;
  updated: string;
  sections: DocSection[];
}

const UPDATED = "29 October 2026";
const site = brand.domain || "this website";
const op = brand.legalName || brand.name;
const email = brand.supportEmail;
const courts = brand.city ? `the courts at ${brand.city}` : "the competent courts in India";
const reach = email ? `email ${email}` : "use the contact page";
const Reach = `${reach.charAt(0).toUpperCase()}${reach.slice(1)}`;

const SEBI_RISK =
  "Investments in the securities market are subject to market risks. Read all the related documents carefully before investing.";
const NOT_REGISTERED = `${op} is not registered with SEBI as a Research Analyst, an Investment Adviser or a stock broker, and ${brand.name} does not give investment advice or research recommendations.`;
const DIRECTORY =
  "The Research Analyst and Investment Adviser directories are compiled from SEBI's public registers for information. A person is listed only because they are on SEBI's register: it does not mean they use, are connected with or are endorsed by us, and it is not a recommendation. Check a person's registration on SEBI's website before you deal with them.";

export const terms: LegalDoc = {
  slug: "terms",
  title: "Terms and Conditions",
  summary: `The rules for using ${brand.name}'s research tools.`,
  updated: UPDATED,
  sections: [
    {
      heading: "1. About these terms",
      body: [
        `These terms govern your use of ${site}, its apps and services (together, "${brand.name}" or "the platform"), operated by ${op} ("we", "us"). By creating an account or using the platform you agree to them. If you do not agree, do not use the platform.`,
      ],
    },
    {
      heading: "2. What the platform is",
      body: [
        `${brand.name} is a market research tools platform for Indian markets. It provides market data, analytics, screeners, strategy labs, backtests, alerts and educational material, so that you can do your own research.`,
        `${NOT_REGISTERED} Nothing on the platform is a recommendation to buy, sell or hold any security, and we do not send calls or tips. We do not hold your money or securities and do not place orders for you.`,
        DIRECTORY,
      ],
    },
    {
      heading: "3. Eligibility and accounts",
      body: [
        {
          list: [
            "You must be at least 18 years old and able to enter into a binding contract under Indian law.",
            "Give accurate information (name, email and mobile number) and keep it up to date. We confirm your email address before your account is used.",
            "Keep your password confidential. You are responsible for activity on your account; tell us at once if you suspect misuse.",
            "An account is for one person. Do not share it or resell access.",
            "We may refuse, suspend or close an account that breaks these terms or the law, or to protect other users.",
          ],
        },
      ],
    },
    {
      heading: "4. Free trial, membership and payment",
      body: [
        "When we offer a free trial (the sign-up page shows it and its length), a new account gets it once per person, with full access. No payment is needed to start it.",
        "After the trial, or from the start when no trial is offered, the platform is available with a paid membership. The price is shown on the pricing page in Indian rupees and includes GST.",
        "You pay online, on the Payment page after you sign in, through our payment gateway (PayU), by any method its secure page offers, such as UPI, card or net banking. Your membership starts as soon as the payment is confirmed, and we email you a receipt. Card, UPI and bank details are entered on PayU's page and never reach us; we never ask for your card number, UPI PIN or bank password.",
        "Membership does not renew automatically. Each paid period ends on the date shown in your account; to continue, you pay again before or after that date. If you do not pay, access pauses and your account is kept.",
        "We may change the price for future periods. A change never applies to a period you have already paid for, and we will tell you before it applies to you.",
      ],
    },
    {
      heading: "5. Acceptable use",
      body: [
        {
          list: [
            "Use the platform for your own research. Do not copy, scrape, resell or redistribute its data, analytics or content without our written permission.",
            "Do not misuse the platform: no unauthorised access, malware, overloading, or attempts to get around security or usage limits.",
            "Do not present the platform's output as investment advice or as a recommendation to others, and do not claim a SEBI registration you do not hold.",
            "Do not use the platform for market manipulation or any activity that breaks securities law.",
          ],
        },
      ],
    },
    {
      heading: "6. Market data, analytics and backtests",
      body: [
        "Market data comes from the public files of NSE and BSE and from broker market-data feeds. Live prices may be delayed, and any data may be incomplete or contain errors. It is provided for information only and is not an official record.",
        "Screeners, strategy scores, backtests, simulations, signals and AI-assisted explanations are research tools. They show how rules would have worked on past data, after the trading costs we model; they are not recommendations and do not predict future returns.",
        `${SEBI_RISK} Past performance, backtests and simulations do not indicate future returns.`,
      ],
    },
    {
      heading: "7. Your decisions",
      body: [
        "You alone decide whether and how to invest or trade, through your own broker. Consider your objectives, finances and risk appetite, and consult a SEBI-registered professional where needed.",
      ],
    },
    {
      heading: "8. Intellectual property",
      body: [
        `The platform, its software, design, analytics and content belong to ${op} or its licensors. Exchange data remains subject to the terms of its providers.`,
      ],
    },
    {
      heading: "9. Availability",
      body: [
        "We aim to keep the platform available and accurate, but we do not promise it will be uninterrupted or error-free. We may change, add or remove features. Planned maintenance is done outside market hours where we can.",
      ],
    },
    {
      heading: "10. Limitation of liability",
      body: [
        'The platform is provided "as is". To the extent the law allows, we are not liable for any investment or trading decision you make, for losses from trading or investing, or for indirect or consequential losses. Our total liability to you for any claim is limited to the fees you paid us in the three months before the claim.',
      ],
    },
    {
      heading: "11. Indemnity",
      body: [
        "You agree to compensate us for claims, losses and costs arising from your breach of these terms or of the law.",
      ],
    },
    {
      heading: "12. Closing your account",
      body: [
        `You may ask us to close and delete your account at any time: ${reach}. We may suspend or end access for a breach of these terms. Records we must keep by law (for example payment and tax records) are kept for the required period after an account closes.`,
      ],
    },
    {
      heading: "13. Governing law and disputes",
      body: [
        `These terms are governed by the laws of India. Subject to the grievance process below, disputes are subject to the exclusive jurisdiction of ${courts}.`,
      ],
    },
    {
      heading: "14. Grievances and contact",
      body: [
        `For questions or complaints, ${reach}. We acknowledge complaints within 2 working days and aim to resolve them within 30 days.`,
      ],
    },
    {
      heading: "15. Changes",
      body: [
        "We may update these terms. The date at the top shows the latest version; material changes will be notified by email or in the app before they take effect.",
      ],
    },
  ],
};

export const privacy: LegalDoc = {
  slug: "privacy",
  title: "Privacy Policy",
  summary: "What personal data we collect, why, who we share it with, how long we keep it, and your rights.",
  updated: "31 October 2026", // signed-in activity (V076)
  sections: [
    {
      heading: "1. Who we are",
      body: [
        `${op} operates ${brand.name} (${site}) and is the Data Fiduciary for your personal data under the Digital Personal Data Protection Act, 2023 (DPDP Act) and the Information Technology Act, 2000.`,
      ],
    },
    {
      heading: "2. What we collect",
      body: [
        {
          list: [
            "Account details: name, email address, mobile number, password (stored only as a secure hash) and, if you give them, city and PIN code.",
            "Your use of the tools: watchlists, alerts, saved ideas and strategies, settings, and the emails we send you.",
            "Payments: amount, date, the period paid for, and the payment gateway's (PayU) transaction id, payment method and, for cards, the masked number it returns (for example 512345XXXXXX2346). We do not collect full card numbers, CVVs, UPI PINs or bank passwords.",
            "Technical data: IP address, device and browser, and pages used, kept in logs for security and to fix problems.",
            "Your activity while signed in: the pages you open (the page address only, never what you type or search for) and the actions you take on your account, such as sign-ins, plan changes and payments. We record this for security, to help you when you contact support, and to improve the service; pages visited are kept for 180 days.",
            "Messages you send us, for example support requests.",
          ],
        },
        "Portfolio uploads (Portfolio X-ray): a CAS statement or broker holdings file you upload, and the statement's password, are processed in memory only to build your X-ray and are never stored or logged. The PDF is never kept. If you choose \"Save this portfolio\", we keep only your holdings (each share's symbol or fund, its ISIN, quantity and average price), never your PAN, name, folio or account numbers, and you can delete them at any time.",
      ],
    },
    {
      heading: "3. Why we use it",
      body: [
        "We process your data on the basis of your consent, given when you create an account, and for legitimate uses the DPDP Act allows (such as meeting legal obligations). We use it:",
        {
          list: [
            "to create and run your account, confirm your email address and provide the tools and alerts you ask for;",
            "to confirm payments, send receipts and keep the records the law requires;",
            "to keep the platform secure, prevent abuse and fix problems;",
            "to send service emails (sign-in, trial and payment messages, alerts you set). Marketing emails are sent only with your consent, and you can opt out at any time.",
          ],
        },
      ],
    },
    {
      heading: "4. Who we share it with",
      body: [
        "We do not sell your personal data. We share it only with service providers who process it for us under contract, and only as needed:",
        {
          list: [
            "cloud hosting for the website, apps and database;",
            "Resend, our email delivery provider, which sends our emails to you;",
            "Google Analytics 4, which measures how the website is used (see section 8);",
            "regulators, courts or law enforcement when the law requires.",
          ],
        },
        "Some providers may process data outside India, as the DPDP Act permits, with safeguards in their contracts.",
      ],
    },
    {
      heading: "5. How long we keep it",
      body: [
        "We keep account data while your account is open. When you ask us to delete your account we erase or anonymise your personal data, except records we must keep by law (for example payment and tax records), which we keep only for the period the law requires. Security logs are kept for a limited time and then deleted.",
      ],
    },
    {
      heading: "6. Security",
      body: [
        "Data is sent over encrypted connections (HTTPS), passwords are hashed, and access to systems is restricted to the people who need it. No system is perfectly secure; if a breach affects your data we will tell you and the Data Protection Board of India as the law requires.",
      ],
    },
    {
      heading: "7. Your rights",
      body: [
        `Under the DPDP Act you may ask for a summary of your personal data and how it is used, correct or update it, have it erased (subject to records we must keep), withdraw your consent at any time, and nominate someone to exercise your rights. To do any of these, ${reach}. If you are not satisfied with our response you may complain to the Data Protection Board of India.`,
      ],
    },
    {
      heading: "8. Cookies, local storage and analytics",
      body: [
        "We use your browser's storage to keep you signed in and to remember preferences such as the theme. We use Google Analytics 4 to understand how the website is used (pages visited, device and approximate location); it sets its own cookies. We do not use advertising trackers. You can block cookies in your browser; the site still works, though you may need to sign in again.",
      ],
    },
    {
      heading: "9. Children",
      body: ["The platform is not meant for anyone under 18, and we do not knowingly collect their data."],
    },
    {
      heading: "10. Grievance officer",
      body: [
        [
          brand.grievanceOfficer ? `Grievance officer: ${brand.grievanceOfficer}.` : "",
          email ? `Email: ${email}.` : "",
          brand.location ? `Location: ${brand.location}.` : "",
          "We acknowledge complaints within 2 working days and resolve them within 30 days.",
        ]
          .filter(Boolean)
          .join(" "),
      ],
    },
    {
      heading: "11. Changes",
      body: ["We will post updates here with a new date, and tell you by email about material changes."],
    },
  ],
};

export const refund: LegalDoc = {
  slug: "refund-policy",
  title: "Cancellation and Refund Policy",
  summary: "How the free trial and membership work, how to stop, and when you get money back.",
  updated: "1 November 2026", // refund window 1 day (owner)
  sections: [
    {
      heading: "1. Free trial",
      body: [
        "When we offer a free trial (the sign-up page shows it and its length), no payment is taken for it, so there is nothing to cancel: if you do not pay after the trial, access simply pauses and your account is kept.",
      ],
    },
    {
      heading: "2. No automatic renewal",
      body: [
        "Membership is paid one period at a time and never renews or charges you automatically. To stop, simply do not pay for the next period; your access continues until the end of the period you have already paid for.",
        `If you want your account closed and your data deleted, ${reach}.`,
      ],
    },
    {
      heading: "3. Refunds",
      body: [
        "Membership gives access to a digital service from the moment it is activated, so fees for a period that has started are not refunded, except in these cases:",
        {
          list: [
            "You paid twice for the same period, or paid more than the price: the extra amount is refunded in full.",
            "We received your payment but could not activate your membership: refunded in full.",
            "You ask within 1 day of your first-ever payment: refunded in full, once per person.",
            "The service was unavailable for most of a paid period because of a fault on our side: refunded in proportion to the time lost.",
          ],
        },
      ],
    },
    {
      heading: "4. How refunds are paid",
      body: [
        "Approved refunds are made through our payment gateway (PayU) to the original method of payment (the card, UPI account or bank account you paid from), usually within 5 to 7 working days of approval. Your bank may take a few more days to show the credit.",
      ],
    },
    {
      heading: "5. Requesting a refund",
      body: [`${Reach} with your registered email, the payment date and the receipt number or transaction id from your receipt email. We reply within 2 working days.`],
    },
  ],
};

export const delivery: LegalDoc = {
  slug: "delivery-policy",
  title: "Service Delivery Policy",
  summary: "How and when you get access to what you pay for.",
  updated: UPDATED,
  sections: [
    {
      heading: "1. Digital service only",
      body: [
        `${brand.name} is an online service. Nothing is shipped: there are no physical goods and no delivery charges.`,
      ],
    },
    {
      heading: "2. When access starts",
      body: [
        "A free trial, when we offer one, starts as soon as you confirm your email address at sign-up.",
        "When you pay online through our payment gateway (PayU), your membership starts as soon as the payment is confirmed, usually at once, and we email you a receipt. You can then use every tool on the website by signing in with your registered email.",
        "Alerts you set (price, idea and strategy-score alerts) are delivered by email and in the app when their conditions are met. Market data follows the exchanges' schedules and may be delayed.",
      ],
    },
    {
      heading: "3. If access does not start",
      body: [
        `If your payment went through but your membership is not active within one working day, ${reach} with the transaction id from your receipt or the payment page. We will activate it or refund you as set out in the Cancellation and Refund Policy. If a payment fails, no money is taken; any amount your bank shows as debited is returned by the payment gateway, usually within 5 to 7 working days.`,
      ],
    },
  ],
};

export const disclaimer: LegalDoc = {
  slug: "disclaimer",
  title: "Disclaimer and Risk Disclosure",
  summary: "Important information about the data, analytics and tools on the platform.",
  updated: UPDATED,
  sections: [
    {
      heading: "Market risk",
      body: [
        SEBI_RISK,
        "Past performance, backtests and simulations do not indicate future returns. Nothing on the platform is a guarantee or promise of returns.",
      ],
    },
    {
      heading: "No investment advice",
      body: [
        `${brand.name} is a market research tools platform. ${NOT_REGISTERED} Screeners, strategy labs, scores, signals, backtests and explanations are tools and education: they are not recommendations to buy, sell or hold any security, and they do not consider your personal circumstances.`,
        "We do not hold client funds or securities and do not place orders.",
      ],
    },
    {
      heading: "Data and its limits",
      body: [
        "Market data comes from the public files of NSE and BSE and from broker market-data feeds. Live prices may be delayed, and data may be incomplete or contain errors. Results of backtests depend on the rules, period and trading costs assumed.",
      ],
    },
    {
      heading: "Directories",
      body: [DIRECTORY],
    },
    {
      heading: "Your decisions",
      body: [
        "Decide on investments based on your own judgement, objectives and risk appetite, and consult a SEBI-registered professional where needed.",
      ],
    },
  ],
};

export const about: LegalDoc = {
  slug: "about",
  title: `About ${brand.name}`,
  summary: brand.tagline,
  updated: UPDATED,
  sections: [
    {
      heading: "What we do",
      body: [
        `${brand.name} makes market research tools for Indian investors and traders. Our four flagship tools are the Options Strategy Lab, Stock Strategy Lab, Market Atlas and Smart Money Tracker, alongside a stock screener, option chain and F&O dashboards, backtests, signal research, alerts, watchlists and live market pages for NSE, BSE and MCX.`,
        "The aim is simple: help you check an idea before you put money on it, in plain language.",
      ],
    },
    {
      heading: "How we work",
      body: [
        {
          list: [
            "Honest numbers: every idea and strategy shows its past record after trading costs, including the periods when it lost.",
            "Plain language: every chart, score and term is explained, so first-time investors can use the tools too.",
            "Data from the source: prices and filings from NSE and BSE public files and broker market feeds, checked every day.",
            "No tips from us: we build tools, not recommendations of our own. You make your own decisions.",
          ],
        },
        "We also keep free directories of SEBI-registered Research Analysts and Investment Advisers, from SEBI's public registers.",
      ],
    },
    {
      heading: "Who runs it",
      body: [
        `${brand.name} is operated by ${op}${brand.location ? `, ${brand.location}` : ""}, India, by people who work in Indian markets. ${NOT_REGISTERED}`,
      ],
    },
  ],
};

/** Every policy page, for the footer and the route list. */
export const LEGAL_DOCS: LegalDoc[] = [about, terms, privacy, refund, delivery, disclaimer];
